import type {
  PortfolioAdditionalStat,
  PortfolioMetricDelta,
  PortfolioMetricFinalOutcome,
  PortfolioMetricInput,
  PortfolioMetricPlanStatus,
  PortfolioMetricProgress,
  PortfolioMetricView,
  PortfolioPlanFrequency,
  PortfolioTrialHeaderInfo,
  TrialInfo,
} from "./types";
import {
  formatNumberWithGrouping,
  formatStatValue,
} from "@/utils/numberFormatting";

export const buildDelta = (
  value?: number | null,
  invertOrOptions:
    | boolean
    | {
        invert?: boolean;
        round?: boolean;
      } = false,
): PortfolioMetricDelta | undefined => {
  if (value === null || value === undefined) return undefined;

  const invert =
    typeof invertOrOptions === "boolean"
      ? invertOrOptions
      : (invertOrOptions.invert ?? false);
  const round =
    typeof invertOrOptions === "boolean"
      ? true
      : (invertOrOptions.round ?? true);

  return {
    value: round ? Math.round(value) : value,
    invert,
  };
};

export const buildPortfolioAdditionalStat = (
  value: string | null,
  rangeLow?: number | null,
  rangeHigh?: number | null,
): PortfolioAdditionalStat => {
  if (!value) return null;

  return {
    label: "PER SITE AVG ± SD",
    value,
    rangeLow: rangeLow ?? null,
    rangeHigh: rangeHigh ?? null,
  };
};

export const buildPerSiteAdditionalStat = (
  average?: number | null,
  sd?: number | null,
  rangeLow?: number | null,
  rangeHigh?: number | null,
  digits = 0,
): PortfolioAdditionalStat =>
  buildPortfolioAdditionalStat(
    formatStatValue(average, sd, digits),
    rangeLow,
    rangeHigh,
  );

export const formatPortfolioAdditionalStatRange = (
  stat: NonNullable<PortfolioAdditionalStat>,
) => {
  if (stat.rangeLow == null || stat.rangeHigh == null) return "—";

  return `${formatNumberWithGrouping(stat.rangeLow)}–${formatNumberWithGrouping(stat.rangeHigh)}`;
};

export const resolvePortfolioTrialHeader = (
  trialId: string,
  trial?: TrialInfo,
): PortfolioTrialHeaderInfo & { isComplete: boolean } => {
  const active = trial?.active ?? false;
  const status = trial?.status ?? (active ? "Active" : "Complete");

  return {
    trial_id: trialId,
    name: trial?.name ?? trialId,
    phase: trial?.phase ? `Phase ${trial.phase}` : "—",
    asset: trial?.asset ?? "—",
    studyType: trial?.studyType ?? "—",
    status,
    statusColor: active ? "green" : "default",
    isComplete: status === "Complete",
  };
};

export const indexTrialsById = (trials: TrialInfo[]) =>
  new Map(trials.map((trial) => [trial.id, trial]));

export const derivePortfolioMetricProgress = (
  numerator: number | null,
  denominator: number | null,
): PortfolioMetricProgress | null => {
  if (numerator === null || denominator === null || denominator === 0) {
    return null;
  }

  return {
    numerator,
    denominator,
    percent: Math.max(0, Math.min(100, (numerator / denominator) * 100)),
  };
};

export const derivePortfolioMetricPlanStatus = ({
  actual,
  planned,
  total,
}: {
  actual: number | null;
  planned: number | null;
  total: number | null;
}): PortfolioMetricPlanStatus | null => {
  if (actual === null || planned === null) return null;

  if (total !== null && actual === planned && actual === total) {
    return "metPlan";
  }

  if (actual < planned) return "behind";
  if (actual > planned) return "ahead";
  return "onPlan";
};

export const derivePortfolioMetricFinalOutcome = ({
  actual,
  total,
}: {
  actual: number | null;
  total: number | null;
}): PortfolioMetricFinalOutcome | null => {
  if (actual === null || total === null) return null;

  if (actual === total) return { kind: "reachedPlan" };
  if (actual < total) return { kind: "belowPlan", difference: total - actual };
  return { kind: "abovePlan", difference: actual - total };
};

export const derivePortfolioPlannedPeriodLabel = (
  date?: Date | null,
  frequency?: PortfolioPlanFrequency | null,
): { visible: string; accessible: string } | null => {
  if (!date || !frequency) return null;

  const shortMonth = date.toLocaleString("en-US", { month: "short" });
  const longMonth = date.toLocaleString("en-US", { month: "long" });
  const day = date.getDate();

  switch (frequency) {
    case "monthly":
      return { visible: `${shortMonth}.`, accessible: `as of ${longMonth}` };
    case "quarterly": {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return { visible: `Q${quarter}`, accessible: `as of Q${quarter}` };
    }
    case "yearly": {
      const year = `${date.getFullYear()}`;
      return { visible: year, accessible: `as of ${year}` };
    }
    case "weekly":
      return {
        visible: `${shortMonth} ${day}`,
        accessible: `as of the week of ${longMonth} ${day}`,
      };
    case "daily":
      return {
        visible: `${shortMonth} ${day}`,
        accessible: `as of ${longMonth} ${day}`,
      };
  }
};

export const derivePortfolioMetricView = (
  input: PortfolioMetricInput,
): PortfolioMetricView => {
  const {
    actual,
    planned,
    total,
    delta30d = null,
    lifecycle,
    planTracked,
  } = input;
  const plannedValue = planned.value;

  const isActivePlanTracked = planTracked && lifecycle === "active";
  const plannedPeriodLabel = isActivePlanTracked
    ? derivePortfolioPlannedPeriodLabel(planned.date, planned.frequency)
    : null;

  const planStatus = isActivePlanTracked
    ? derivePortfolioMetricPlanStatus({ actual, planned: plannedValue, total })
    : null;

  const finalOutcome =
    lifecycle === "final"
      ? derivePortfolioMetricFinalOutcome({ actual, total })
      : null;

  return {
    lifecycle,
    planTracked,
    actual,
    planned: plannedValue,
    total,
    delta30d: delta30d ?? null,
    planStatus,
    finalOutcome,
    actualProgress: planTracked
      ? derivePortfolioMetricProgress(actual, total)
      : null,
    plannedMarkerProgress: isActivePlanTracked
      ? derivePortfolioMetricProgress(plannedValue, total)
      : null,
    plannedPeriodLabel: plannedPeriodLabel?.visible ?? null,
    plannedPeriodAccessibleLabel: plannedPeriodLabel?.accessible ?? null,
  };
};

export const formatPortfolioMetricValue = (
  value: number | null,
  formatter: (value: number) => string = formatNumberWithGrouping,
) => (value === null ? "-" : formatter(value));

export const describePortfolioMetricPlanStatus = (
  status: PortfolioMetricPlanStatus | null,
): string | null => {
  switch (status) {
    case "behind":
      return "Behind plan";
    case "onPlan":
      return "On plan";
    case "ahead":
      return "Ahead of plan";
    case "metPlan":
      return "Met plan";
    default:
      return null;
  }
};

export const describePortfolioMetricFinalOutcome = (
  outcome: PortfolioMetricFinalOutcome | null,
  formatter: (value: number) => string = formatNumberWithGrouping,
  suffix?: string,
): string | null => {
  if (!outcome) return null;

  const fmt = (value: number) => `${formatter(value)}${suffix ?? ""}`;

  switch (outcome.kind) {
    case "reachedPlan":
      return "Reached plan";
    case "belowPlan":
      return `${fmt(outcome.difference)} below plan`;
    case "abovePlan":
      return `${fmt(outcome.difference)} above plan`;
  }
};

export const describePortfolioMetricView = (
  view: PortfolioMetricView,
  label: string,
  formatter: (value: number) => string = formatNumberWithGrouping,
  suffix?: string,
): string => {
  const format = (value: number | null) =>
    formatPortfolioMetricValue(value, formatter) +
    (value !== null && suffix ? suffix : "");
  const parts = [label, `actual ${format(view.actual)}`];

  if (view.planTracked && view.lifecycle === "active") {
    const plannedText = view.plannedPeriodAccessibleLabel
      ? `planned ${format(view.planned)} ${view.plannedPeriodAccessibleLabel}`
      : `planned ${format(view.planned)}`;
    parts.push(plannedText);
  }

  if (view.total !== null) {
    parts.push(`total ${format(view.total)}`);
  }

  if (view.lifecycle === "final") {
    parts.push("final");
    const outcomeText = describePortfolioMetricFinalOutcome(
      view.finalOutcome,
      formatter,
      suffix,
    );
    if (outcomeText) parts.push(outcomeText);
  }
  // Plan-status text is intentionally omitted while the visible status pill is
  // disabled in PortfolioMetricCard; restore both together.

  return parts.join(", ");
};
